import { Link } from 'react-router-dom';
import { LoadFailure } from '../components/LoadFailure.tsx';
import { TriageRecord } from '../components/tasks/TriageRecord.tsx';
import { ChecksNotDeposited } from '../components/FurtherMarshallingPanel.tsx';
import { CompletionSources } from '../components/tasks/CompletionSources.tsx';
import { useKeptState } from '../components/OpenTabs.tsx';
import { formatDate } from '../dates.ts';
import { BAND_WORDS, useDeathCertificates, useHeld, useReleaseTriage, useTriage, type CommissionNoOrderRow, type DeathCertificate, type TriageRow } from '../api/triage.ts';
import { useRecordDocument } from '../components/RecordDocument.tsx';
import { severityWord, useAssignees, type Department } from '../api/taskQueries.ts';
import {
  TRIAGE_FRESH, ageOf, assigneeGroups, countByAge, defaultAssignee, keptDepartment, keptSelection, releasable, summarise, triageKey,
  type TriageKept,
} from '../tasks/triage.ts';
import { field, label } from '../components/contacts/Sheet.tsx';
import { Refusal, chip, go, plain } from '../components/tasks/Fields.tsx';

/**
 * Triage (146, Arthur's rulings 2-5, 14, 15). The backlog is held: no task exists until a recorded act releases it.
 * COUNTS FIRST: the held summary by department and band, and the withheld (the hidden rules and exempt initial visits;
 * A-#83 A2: provisional deadlines are not held, and nothing here says "provisional"), then the rows. Overdue deadlines
 * and a death's backlog go through triage only, per selection, to a person or the queue; closing one goes through the
 * deadline's own paths, not here. The release act (a whole band, tiers 1-2 only) is its own page, Release held work
 * (A-#144). The server decides who may do each and refuses what is not allowed; its sentence is shown as it is.
 */
export function Triage() {
  const held = useHeld();
  const rows = held.data?.rows ?? [];
  // The server's own answer: which departments the reader may triage. It enforces it regardless. The release act has
  // its own page, Release held work (A-#144).
  const mayTriage = held.data?.may_triage ?? [];

  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-[96rem] lg:px-6 lg:py-6">
        <header className="page-head">
          <h1 className="page-title">Triage</h1>
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/tasks" className={`${plain} inline-flex items-center`}>My tasks</Link>
            <Link to="/tasks/queue" className={`${plain} inline-flex items-center`}>Department queue</Link>
          </div>
          <p className="mt-1 max-w-[60rem] text-sm text-neutral-700">
            Deadlines from before the cutover are held: none is anyone's task until it is released here. Overdue ones,
            and those that follow a death recorded before the cutover, are released one selection at a time.
          </p>
        </header>
        <div className="page-body px-4 pb-4 lg:px-5">
          {held.error && !held.data ? (
            <LoadFailure what="The held deadlines" error={held.error} onRetry={() => void held.refetch()} retrying={held.isFetching} />
          ) : !held.data ? (
            <p className="py-6 text-neutral-700">Loading…</p>
          ) : (
            <>
              <HeldCounts rows={rows} />
              <CompletionSources rules={held.data.unsourced ?? []} />
              <TriageRows departments={mayTriage} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** The held work by department and band, counts first (ruling 5); shared with Release held work (A-#144). */
export function HeldCounts({ rows }: { rows: Parameters<typeof summarise>[0] }) {
  const summary = summarise(rows);
  return (
    <section aria-labelledby="counts" className="mt-3">
      <h2 id="counts" className="text-sm font-bold tracking-wide text-neutral-700 uppercase">Held, by department</h2>
      {summary.length === 0 ? <p className="py-3 text-neutral-700">Nothing is held.</p> : (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full min-w-[40rem] border-collapse text-sm">
            <thead><tr className="border-b border-neutral-300 text-left text-neutral-700">
              <th className="py-2 pr-3">Department</th>
              <th className="py-2 pr-3 text-right">{BAND_WORDS.within_14}</th>
              <th className="py-2 pr-3 text-right">{BAND_WORDS.later}</th>
              <th className="py-2 pr-3 text-right">{BAND_WORDS.overdue}</th>
              <th className="py-2 pr-3 text-right">Triage only (other)</th>
              <th className="py-2 pr-3 text-right">Withheld</th>
            </tr></thead>
            <tbody>
              {summary.map((s) => (
                <tr key={s.department} className="border-b border-neutral-200 text-neutral-900">
                  <td className="py-2 pr-3 font-semibold">{s.department}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{s.byBand.within_14.held}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{s.byBand.later.held}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{s.byBand.overdue.held + s.byBand.overdue.triage_only}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{s.byBand.within_14.triage_only + s.byBand.later.triage_only}</td>
                  <td className="py-2 pr-3 text-right text-neutral-800">
                    {s.withheld.hidden} no completion source · {s.withheld.exempt_visit} exempt first visits
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-1 text-sm text-neutral-700">
        Withheld deadlines are never released by any act: those from rules with no completion source until they
        have one, and an exempt client's first visit never.
      </p>
    </section>
  );
}

/**
 * Triage per selection: the department's overdue and death-backlog rows, counted by age first. Since 156 (A-#246) the
 * deaths with no date recorded are NOT a department's rows: they are their own section (A-#274), a sibling drawn
 * between "Held, by department" and "To triage", never inside it, with the live count this reader may see, and hidden
 * when there are none. They come with the department's answer, so the two sections are drawn here together.
 */
function TriageRows({ departments }: { departments: Department[] }) {
  // P-30 (A-#288): what the page shows is kept while its tab is open, in memory only, so the Triage tab (or Back)
  // returns to it exactly: the department, the ticked rows, the person chosen and the reason typed.
  const [kept, setKept] = useKeptState<TriageKept>('triage', TRIAGE_FRESH);
  const department = keptDepartment(kept.department, departments);
  const q = useTriage(department);
  const triageRows = q.data?.rows ?? [];
  const selected = keptSelection(kept.selected, q.data?.rows);
  const setSelected = (next: Set<string>) => setKept((k) => ({ ...k, selected: [...next] }));
  const clearSelection = () => setKept((k) => ({ ...k, selected: [], pick: null, reason: '' }));
  const deaths = q.data?.deaths_no_date;
  const noOrder = q.data?.commission_no_order;
  const ages = countByAge(triageRows);
  const toggle = (id: string) => { const n = new Set(selected); n.has(id) ? n.delete(id) : n.add(id); setSelected(n); };
  const chooseAge = (age: string) => setSelected(new Set(triageRows.filter((r) => releasable(r) && ageOf(r.days_overdue) === age).map((r) => r.deadline_id!)));
  return (
    <>
    {deaths && deaths.count > 0 && <DeathsNoDate count={deaths.count} rows={deaths.rows} />}
    {/* 166 (P-33 R3 7): the Finance view's list of checks in hand, not deposited after 10 business days */}
    {department === 'Finance' && <ChecksNotDeposited />}
    {noOrder?.shown && noOrder.count > 0 && <CommissionNoOrder count={noOrder.count} rows={noOrder.rows} />}
    <section aria-labelledby="rows" className="mt-5">
      <h2 id="rows" className="text-sm font-bold tracking-wide text-neutral-700 uppercase">To triage</h2>
      {departments.length > 1 && (
        <div role="tablist" aria-label="Department" className="mt-2 flex flex-wrap gap-2">
          {departments.map((d) => (
            <button key={d} type="button" role="tab" aria-selected={d === department} className={chip(d === department)}
                    onClick={() => setKept({ ...TRIAGE_FRESH, department: d })}>{d}</button>
          ))}
        </div>
      )}
      {q.error && !q.data ? <Refusal error={q.error} />
        : !q.data ? <p className="py-3 text-neutral-700">{department ? 'Loading…' : 'Nothing is held.'}</p>
        : triageRows.length === 0 ? <p className="py-3 text-neutral-700">Nothing in {department} is waiting for triage.</p>
        : (
          <>
            <ul aria-label="By age" className="mt-2 flex flex-wrap gap-2">
              {ages.filter((a) => a.count > 0).map((a) => (
                <li key={a.age}>
                  <button type="button" className={plain} onClick={() => chooseAge(a.age)}>
                    {a.age} overdue: <span className="tabular-nums">{a.count}</span> (select)
                  </button>
                </li>
              ))}
            </ul>
            <ol className="mt-2 divide-y divide-neutral-200 rounded-lg border border-neutral-300">
              {triageRows.filter(releasable).map((r) =>
                <Row key={triageKey(r)} row={r} on={selected.has(r.deadline_id!)} onToggle={() => toggle(r.deadline_id!)} />)}
            </ol>
            {selected.size > 0 && department && (
              <ReleaseSelection department={department} ids={[...selected]} rows={triageRows.filter((r) => r.deadline_id !== null && selected.has(r.deadline_id))}
                                pick={kept.pick} setPick={(p) => setKept((k) => ({ ...k, pick: p }))}
                                reason={kept.reason} setReason={(r) => setKept((k) => ({ ...k, reason: r }))}
                                onDone={clearSelection} />
            )}
          </>
        )}
    </section>
    </>
  );
}

/**
 * A row's client (P-30, A-#284): the name opens the client in its tab and switches to it. A Link, because the tabs'
 * open() only adds a tab: going to /clients/<id> adds it and shows it, as from anywhere else, and pushes, so Back
 * returns here. Triage's own tab, and what it was showing, stay (its kept state). Inside a row's <label> it is
 * interactive content, so it never ticks the row; stopPropagation keeps the click off the row besides.
 */
function ClientName({ id, name, clientRef }: { id: string; name: string; clientRef: string }) {
  return (
    <Link to={`/clients/${id}`} onClick={(e) => e.stopPropagation()}
          className="inline-flex min-h-[44px] items-center gap-1 font-semibold text-brand-text underline underline-offset-2">
      {name} <span className="font-mono">{clientRef}</span>
    </Link>
  );
}

/**
 * A row: its box, its words, and below them (P-31) the record that completes its deadline, as that record's own form,
 * or why it cannot be closed here (TriageRecord). The record piece is outside the <label>, so it never ticks the row.
 */
function Row({ row: r, on, onToggle }: { row: TriageRow; on: boolean; onToggle: () => void }) {
  const word = severityWord(r.severity);
  return (
    <li>
      <label className="flex min-h-[52px] cursor-pointer items-center gap-3 px-3 py-2 hover:bg-neutral-50">
        <input type="checkbox" className="size-5 shrink-0" checked={on} onChange={onToggle} />
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            {word && <span className="rounded border border-neutral-400 px-1.5 text-xs font-bold uppercase text-neutral-800">{word}</span>}
            <span className="font-semibold text-neutral-900">{r.label}</span>
          </span>
          <span className="block text-sm text-neutral-700">
            <ClientName id={r.client_id} name={r.display_name} clientRef={r.client_ref} /> · {r.why === 'death' ? 'After a death recorded before the cutover' : 'Overdue'}
            {r.is_statutory ? ' · Statutory' : ''}
          </span>
        </span>
        <span className="shrink-0 text-right text-sm text-neutral-900 tabular-nums">
          {r.due_date ? formatDate(r.due_date) : 'No date'}
          {r.days_overdue !== null && r.days_overdue > 0 && <span className="block font-semibold text-critical-edge">{r.days_overdue} days overdue</span>}
        </span>
      </label>
      <TriageRecord row={r} />
    </li>
  );
}

/**
 * 156 (A-#246, Arthur, 17:38): the deaths with no date recorded, once, with the live count this reader may see. The
 * rows are the server's (triage_deaths_no_date(), ruling B's own who-sees rule), the same whatever department is
 * chosen. Its own section with the SAME heading as "Held, by department" and "To triage" (A-#274, Arthur: drawn as a
 * row's text, it "looks like a member of its own list"): the heading, the sentence, then the rows in their border.
 * D-127: exported, and drawn the same on the Department queue page, where Legal sees it (DepartmentQueue.tsx).
 */
export function DeathsNoDate({ count, rows }: { count: number; rows: TriageRow[] }) {
  return (
    <section aria-labelledby="deaths-no-date" className="mt-5">
      <h2 id="deaths-no-date" className="text-sm font-bold tracking-wide text-neutral-700 uppercase">Deaths with no date recorded ({count})</h2>
      <p className="mt-1 text-sm text-neutral-700">
        Each client's final report cannot be raised until the date of death is recorded on the client's Legal section.
      </p>
      <ol className="mt-2 divide-y divide-neutral-200 rounded-lg border border-neutral-300">
        {rows.map((r) => <NoDateRow key={triageKey(r)} row={r} />)}
      </ol>
      <DeathCertificatesOnFile rows={rows} />
    </section>
  );
}

/**
 * 167 (P-24 26): the clients the commission tracker covers with no acting order on file: a red flag, never a quiet
 * status, for intake, Legal and tiers 1-2 (the server sends the rows only to them). Each opens the client's Legal
 * section, where the order is recorded. The same words as the tracker's red line.
 */
function CommissionNoOrder({ count, rows }: { count: number; rows: CommissionNoOrderRow[] }) {
  return (
    <section aria-labelledby="commission-no-order" className="mt-5">
      <h2 id="commission-no-order" className="text-sm font-bold tracking-wide text-neutral-700 uppercase">No acting order on file ({count})</h2>
      <p className="mt-1 text-sm font-semibold text-critical-edge">The commission cannot be tracked until the order granting the powers is recorded.</p>
      <ol className="mt-2 divide-y divide-neutral-200 rounded-lg border border-neutral-300">
        {rows.map((r) => (
          <li key={r.client_id} className="flex min-h-[44px] flex-wrap items-center gap-x-3 px-3 py-2">
            <Link to={`/clients/${r.client_id}/legal`} className="link font-semibold">{r.display_name}</Link>
            <span className="tabular-nums text-neutral-600">{r.client_ref}</span>
            <span className="text-neutral-800">{r.label}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * D-116 (Arthur, 29 October: "one place" to see the death certificates). Asked only when the reader presses Show, since
 * each client's list is an audited Documents look. Found by the file's NAME (the inventory's rule), so a certificate
 * filed under another name is not found, and the words say so. Each opens through P-20's "View document", the
 * Documents section's own audited preview: nothing is downloaded or copied here, and there is no other way in.
 */
function DeathCertificatesOnFile({ rows }: { rows: TriageRow[] }) {
  const certs = useDeathCertificates();
  const found = new Map((certs.data?.clients ?? []).map((c) => [c.client_id, c.certificates]));
  const clients = [...new Map(rows.map((r) => [r.client_id, r])).values()];
  return (
    <div className="mt-3 rounded-lg border border-neutral-300 p-3">
      <h3 className="font-semibold text-neutral-900">Death certificates on file</h3>
      <p className="text-sm text-neutral-700">Found in each client's Documents by the file's name. A certificate filed under another name is not found here.</p>
      {!certs.data ? (
        <button type="button" className={`${plain} mt-2`} disabled={certs.isPending} onClick={() => { if (!certs.isPending) certs.mutate(); }}>
          {certs.isPending ? 'Looking in Documents…' : 'Show the death certificates on file'}</button>
      ) : !certs.data.holds_documents ? (
        <p className="mt-2 text-neutral-900">The certificate is in Documents, which your roles do not open.</p>
      ) : (
        <ul className="mt-2 space-y-2">
          {clients.map((r) => {
            const list = found.get(r.client_id) ?? [];
            return (
              <li key={r.client_id} data-certificates={r.client_id}>
                <ClientName id={r.client_id} name={r.display_name} clientRef={r.client_ref} />
                {list.length === 0 ? <span className="block text-sm text-neutral-700">No death certificate found by its file name.</span> : (
                  <ul className="mt-1 space-y-1">
                    {list.map((c) => <CertificateLine key={c.copy_id} clientId={r.client_id} cert={c} />)}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {certs.error && <Refusal error={certs.error} />}
    </div>
  );
}

function CertificateLine({ clientId, cert }: { clientId: string; cert: DeathCertificate }) {
  const doc = useRecordDocument(clientId, { upload_id: null, upload_name: null, upload_status: null, clio_copy_id: cert.copy_id }, 'death certificate');
  return (
    <li className="flex flex-wrap items-center gap-3 text-sm text-neutral-900">
      <span className="min-w-0 break-words">{cert.filename}{cert.date ? ` · ${formatDate(cert.date)}` : ''}</span>
      {cert.status === 'verified' ? doc.control : <span className="text-neutral-700">Not yet checked for viruses, so it cannot be opened yet</span>}
      {cert.status === 'verified' ? doc.preview : null}
    </li>
  );
}

/**
 * 146's F2: a final report and account that cannot be raised, because the date of death is not recorded. Never
 * selectable; it says what to do, and goes to the client's Legal section, where the date is recorded (156). No priority
 * badge: 155 stores it Normal and nothing automatic sets Emergency.
 */
function NoDateRow({ row: r }: { row: TriageRow }) {
  return (
    <li data-no-date={r.client_id} className="flex min-h-[52px] flex-wrap items-center gap-3 px-3 py-2">
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-neutral-900">{r.label}</span>
        </span>
        <span className="block text-sm text-neutral-700">
          <ClientName id={r.client_id} name={r.display_name} clientRef={r.client_ref} /> · Statutory · cannot be released until the date of death is recorded
        </span>
      </span>
      <Link to={`/clients/${r.client_id}/legal`} className={`${go} inline-flex items-center`}>Record the date of death</Link>
    </li>
  );
}

const STARTS: Record<'primary' | 'queue' | 'mixed' | 'unlisted' | 'none', string> = {
  primary: "Starts at the client's Primary in this department, as a band release would give it.",
  queue: 'Starts at the queue: no client selected has a Primary in this department.',
  mixed: 'The selected clients have different Primaries here: choose one, or release them client by client.',
  unlisted: "The client's Primary is not listed for this department: choose who takes them.",
  none: '',
};

function ReleaseSelection({ department, ids, rows, pick, setPick, reason, setReason, onDone }: {
  department: Department; ids: string[]; rows: TriageRow[];
  // P-30: the person chosen and the reason typed are Triage's kept state, so they survive leaving by a client's tab
  pick: TriageKept['pick']; setPick: (p: TriageKept['pick']) => void; reason: string; setReason: (r: string) => void;
  onDone: () => void;
}) {
  const people = useAssignees(department, 'assign');
  const release = useReleaseTriage();
  // A-#145 (b): the bands' rule preselected; a choice made here is kept only for the selection it was made on.
  const key = [...ids].sort().join(',');
  const start = defaultAssignee(rows, people.data?.people ?? []);
  const to = pick?.key === key ? pick.to : people.data ? start.to : '';
  const setTo = (value: string) => setPick({ key, to: value });
  const groups = assigneeGroups(people.data?.people ?? []);
  if (release.data) {
    return (
      <div className="mt-3 space-y-2 rounded-lg border border-neutral-300 px-3 py-3">
        <p role="status" className="font-semibold text-neutral-900">{release.data.released} {release.data.released === 1 ? 'task' : 'tasks'} made.</p>
        <button type="button" className={plain} onClick={() => { release.reset(); onDone(); }}>Done</button>
      </div>
    );
  }
  return (
    <div className="sticky bottom-0 mt-3 space-y-3 rounded-lg border border-neutral-300 bg-panel px-3 py-3 shadow-lg">
      <p className="font-semibold text-neutral-900">{ids.length} selected in {department}.</p>
      <label className={label}>Give them to
        <select value={to} onChange={(e) => setTo(e.target.value)} className={field}>
          <option value="">Choose…</option>
          <option value="queue">The {department} queue</option>
          {groups.own.length > 0 && (
            <optgroup label={department}>
              {groups.own.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.role})</option>)}
            </optgroup>
          )}
          {groups.others.length > 0 && (
            <optgroup label="Others (tiers 1-2)">
              {groups.others.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.role})</option>)}
            </optgroup>
          )}
        </select>
      </label>
      {people.data && pick?.key !== key && STARTS[start.why] && <p data-starts={start.why} className="text-sm text-neutral-700">{STARTS[start.why]}</p>}
      <label className={label}>Why
        <input type="text" maxLength={2000} value={reason} onChange={(e) => setReason(e.target.value)} className={field} />
      </label>
      <p className="text-sm text-neutral-700">To close a deadline instead, open the client and use the deadline's own paths.</p>
      <Refusal error={release.error} />
      <div className="flex flex-wrap gap-2">
        <button type="button" className={go} disabled={to === '' || !reason.trim() || release.isPending}
                onClick={() => release.mutate({ deadlineIds: ids, toStaffId: to === 'queue' ? null : to, reason: reason.trim() })}>
          Release {ids.length} as {ids.length === 1 ? 'a task' : 'tasks'}
        </button>
        <button type="button" className={plain} onClick={onDone}>Clear the selection</button>
      </div>
    </div>
  );
}

